import {
	checkCookieExists,
	getCookieValue,
	getItemFromSessionStorage,
	setItemInSessionStorage
} from '@utils/web-storage';
import { flattenQueryParam, includesRequiredQueryParams } from '@utils/request';
import { NavigationGuard, RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import {
	storageKeyLoginRedirect,
	storageKeyVerifyPasswordCancel,
	storageKeyVerifyPasswordRedirect
} from '@constants/storage-keys';
import { datadogRum } from '@datadog/browser-rum';
import { getNewAccessToken } from '@api/login';
import { handleAuthentication } from './auth-guard';
import { ThirdPartyAuthType } from 'types/third-party-auth';
import { TokenScope } from 'types/security';
import { useAppStore } from '@stores/app';
import { useInvestmentEntityStore } from '@stores/investment-entity';
import { useUserStore } from '@stores/user';

export function includesVerifyRoute(pathString: string | null): boolean {
	if (pathString) {
		return ['/signup/verify-signup-checkout', '/signup/verify-email'].some((element) =>
			pathString.includes(element)
		);
	}
	return false;
}

export const refreshTokenAndRedirect = async (
	redirectTo: RouteLocationNormalized,
	from: RouteLocationNormalized
): Promise<ReturnType<NavigationGuard>> => {
	const appStore = useAppStore();

	try {
		const refreshToken = await getNewAccessToken();
		const userStore = useUserStore();
		await appStore.setUserAuthData(refreshToken);
		if (!userStore.user) {
			await userStore.getUser();
		}

		if (!refreshToken.scope || !refreshToken.access_token) {
			saveLoginRedirect(redirectTo);
			return { name: 'login' };
		}
	} catch {
		appStore.resetAuthData();
		saveLoginRedirect(redirectTo);
	}

	return handleAuthentication(redirectTo, from);
};

export const handleThirdPartyScope = async (
	to: RouteLocationNormalized,
	from: RouteLocationNormalized,
	minTokenScopeRequired: TokenScope
): Promise<ReturnType<NavigationGuard>> => {
	if (minTokenScopeRequired === 'THIRD_PARTY_ACCESS') {
		const userStore = useUserStore();
		if (!userStore.user) {
			await userStore.getUser();
		}
		return true;
	} else {
		const appStore = useAppStore();
		datadogRum.addAction('forbidden route', { from: from.name, to: to.name, scope: appStore.tokenScope });
		return { name: 'forbidden' };
	}
};

export const handleGoogleAppleAuthSetup = async (
	to: RouteLocationNormalized,
	type: ThirdPartyAuthType
): Promise<ReturnType<NavigationGuard>> => {
	if (!to.matched.some((route) => (route.name as string)?.includes(`login-verify-link-${type}`))) {
		return { name: `login-verify-link-${type}` };
	} else {
		return true;
	}
};

export const handleTwoFactor = async (to: RouteLocationNormalized): Promise<ReturnType<NavigationGuard>> => {
	if (!to.matched.some((route) => (route.name as string)?.includes('login-two-factor'))) {
		return { name: 'login-two-factor' };
	} else {
		return true;
	}
};

export const handlePrivilegedAccess = async (
	tokenScope: TokenScope,
	nextRoute: RouteLocationNormalized,
	cancelRoute: RouteLocationNormalized
): Promise<ReturnType<NavigationGuard>> => {
	const userStore = useUserStore();
	const passwordVerified = tokenScope === 'PRIVILEGED_ACCESS' || tokenScope === 'ADVISOR_PRIVILEGED_ACCESS';
	if (!userStore.hasPasswordLoginMethod) {
		return { name: 'profile-personal-information-view' };
	} else if (!passwordVerified) {
		setItemInSessionStorage(storageKeyVerifyPasswordRedirect, nextRoute.name as string);
		setItemInSessionStorage(
			storageKeyVerifyPasswordCancel,
			(cancelRoute.name as string) || 'profile-personal-information-view'
		);
		return { name: 'password-verify' };
	} else {
		return true;
	}
};

export const handleInvestmentEntityQuery = async (
	to: RouteLocationNormalized
): Promise<ReturnType<NavigationGuard>> => {
	const userStore = useUserStore();
	const investmentEntityId = flattenQueryParam(to.query.investmentEntityId) ?? '';
	const entityList = userStore.user?.entityDetails?.investmentEntities ?? [];
	const toRouteName = (to.name ?? '') as string;
	delete to.query.investmentEntityId;

	const selectorRoute = {
		name: 'investment-entity-selector',
		query: to.query,
		state: { ...to.params, redirectRoute: toRouteName }
	};

	if (entityList.length <= 1) {
		return to;
	}

	if (investmentEntityId === 'select') {
		return selectorRoute;
	}

	const investmentEntityStore = useInvestmentEntityStore();

	if (investmentEntityId !== investmentEntityStore.investmentEntityId) {
		try {
			return await userStore.updateSelectedEntityWithoutReload({
				investmentEntityId,
				nextRoute: { route: to, return: true }
			});
		} catch (e) {
			return selectorRoute;
		}
	}

	return to;
};

// Check if the route requires entity selection after login (for the IR team macros)
export const routesThatRequireEntitySelectionPostLogin = [
	'/account/investment-accounts/bank-accounts/manage',
	'/account/investment-accounts/personal-information/manage',
	'/account/investment-accounts/advanced-options/view',
	'/account/investment-accounts/recurring-investment-settings/manage',
	'/account/investment-accounts/recurring-investment-settings/view',
	'/account/investment-accounts/ria-dividend-reinvestment/manage',
	'/account/investment-accounts/dividend-reinvestment/manage',
	'/account/investment-accounts/investment-plan/view',
	'/account/investment-accounts/investment-goal/manage'
];

const isUnverifiedRoute = (to: RouteLocationNormalized): boolean => {
	const appStore = useAppStore();
	return !appStore.isVerified && to.meta?.minTokenScopeRequired === 'UNVERIFIED';
};

const isUserUnverified = (to: RouteLocationNormalized, loginRedirectIncludesVerifyRoute: boolean): boolean => {
	const appStore = useAppStore();
	return !appStore.isVerified && to.meta?.minTokenScopeRequired !== 'UNVERIFIED' && !loginRedirectIncludesVerifyRoute;
};

const isAdvisorRoute = (minTokenScopeRequired: TokenScope): boolean => {
	return minTokenScopeRequired === 'ADVISOR_ACCESS' || minTokenScopeRequired === 'ADVISOR_PRIVILEGED_ACCESS';
};

export const handleAuthenticatedScope = async (
	to: RouteLocationNormalized,
	from: RouteLocationNormalized,
	minTokenScopeRequired: TokenScope
): Promise<ReturnType<NavigationGuard>> => {
	const loginRedirect = getItemFromSessionStorage('login-redirect');
	const additionalAllowedScopes = to.meta?.additionalAllowedScopes ?? [];
	const loginRedirectIncludesVerifyRoute = loginRedirect ? includesVerifyRoute(loginRedirect) : false;
	const userNeedsToVerifyEmail = isUserUnverified(to, loginRedirectIncludesVerifyRoute);
	const appStore = useAppStore();
	const userStore = useUserStore();

	if (!userStore.user) {
		await userStore.getUser();
	}

	if (isUnverifiedRoute(to)) {
		return true;
	} else if (userNeedsToVerifyEmail) {
		return { name: 'unverified-email' };
	} else if (appStore.isAdvisor) {
		if (
			isAdvisorRoute(minTokenScopeRequired) ||
			additionalAllowedScopes.includes('ADVISOR_ACCESS') ||
			additionalAllowedScopes.includes('ADVISOR_READ_ACCESS')
		) {
			return true;
		} else {
			datadogRum.addAction('forbidden route', { from: from.name, to: to.name, scope: appStore.tokenScope });
			return { name: 'forbidden' };
		}
	} else {
		const includesInvestmentEntityQuery = includesRequiredQueryParams(['investmentEntityId'], to);

		if (isAdvisorRoute(minTokenScopeRequired)) {
			datadogRum.addAction('forbidden route', { from: from.name, to: to.name, scope: appStore.tokenScope });
			return { name: 'forbidden' };
		} else if (includesInvestmentEntityQuery) {
			return handleInvestmentEntityQuery(to);
		} else {
			return true;
		}
	}
};

export const handleWebviewRoute = async (): Promise<ReturnType<NavigationGuard>> => {
	const appStore = useAppStore();
	const accessToken = getCookieValue('access_token');
	appStore.isMobileWebview = true;

	if (!appStore.isAuthenticated && !!accessToken) {
		await appStore.authenticateFromMobileWebview();
	}
};

export const handleUnauthenciatedState = async (
	to: RouteLocationNormalized,
	newRoute?: boolean | RouteLocationRaw
): Promise<ReturnType<NavigationGuard>> => {
	const userHasRefreshToken = checkCookieExists('userLoggedIn');

	if (newRoute === false) {
		return newRoute;
	}

	if (userHasRefreshToken) {
		const appStore = useAppStore();
		try {
			const refreshToken = await getNewAccessToken();
			await appStore.setUserAuthData(refreshToken);
			return newRoute ?? to;
		} catch {
			appStore.resetAuthData();
			saveLoginRedirect(to);
			return { name: 'login' };
		}
	}

	saveLoginRedirect(to);
	return { name: 'login' };
};

export const saveLoginRedirect = (to: RouteLocationNormalized): void => {
	if (to.redirectedFrom && routesThatRequireEntitySelectionPostLogin.includes(to.redirectedFrom?.path)) {
		setItemInSessionStorage(storageKeyLoginRedirect, to.redirectedFrom?.path);
	} else {
		setItemInSessionStorage(storageKeyLoginRedirect, to.fullPath);
	}
};
